// structure-graph.ts — Logique pure du graphe des structures (organisation).
//
// Extrait de page-admin-structures pour être testable hors DOM (vitest). Une
// structure « dépend de » 0..N parents (graphe orienté, parents multiples).

export interface StructureNode {
  id: number;
  parent_ids: number[];
}

/** parentId -> liste d'IDs enfants (ordre d'apparition, non trié). */
export function childrenMap(structures: ReadonlyArray<StructureNode>): Map<number, number[]> {
  const m = new Map<number, number[]>();
  for (const s of structures) {
    for (const pid of s.parent_ids || []) {
      const arr = m.get(pid) ?? [];
      arr.push(s.id);
      m.set(pid, arr);
    }
  }
  return m;
}

/** IDs de toutes les structures atteignables depuis `rootId` en suivant les
 *  arêtes parent→enfant (root exclu). Sert au contrôle anti-cycle et au
 *  grisage du sous-arbre déplacé. Robuste aux cycles éventuels (set `seen`). */
export function descendantIds(
  structures: ReadonlyArray<StructureNode>,
  rootId: number,
): Set<number> {
  const children = childrenMap(structures);
  const seen = new Set<number>();
  const stack = [...(children.get(rootId) || [])];
  while (stack.length) {
    const cur = stack.pop();
    if (cur === undefined || seen.has(cur)) continue;
    seen.add(cur);
    stack.push(...(children.get(cur) || []));
  }
  return seen;
}

/**
 * Calcule le nouveau jeu de parents d'une structure après une opération de
 * drag & drop.
 *
 * - `fromParent` : parent sous lequel la structure était affichée (null si
 *   elle était racine).
 * - `targetParent` : structure cible du dépôt (null = détachement / zone racine).
 * - `add` : true = Maj+glisser (AJOUTE le lien, conserve `fromParent`).
 *           false = déplacement (RETIRE `fromParent`).
 *
 * Exemple « 1 ← 2 ← 3, couper le lien 1-2 » : computeRelinkParents([1], 1,
 * null, false) === [] pour la structure 2 ; la structure 3 (parents [2])
 * n'est pas touchée → le lien 2-3 est conservé.
 */
export function computeRelinkParents(
  current: ReadonlyArray<number>,
  fromParent: number | null,
  targetParent: number | null,
  add: boolean,
): number[] {
  let parents = [...current];
  if (!add && fromParent !== null) parents = parents.filter((p) => p !== fromParent);
  if (targetParent !== null && !parents.includes(targetParent)) parents.push(targetParent);
  return parents;
}

/**
 * Une action est-elle visible quand on filtre la liste `#actions` par la
 * structure `targetId` ?
 *
 * Vrai si : lien direct vers `targetId`, OU action escaladée depuis une
 * structure fille DONT `targetId` est un parent direct (escalade 1 niveau).
 *
 * `parentsOf(id)` renvoie les parents directs d'une structure.
 */
export function actionVisibleForStructure(
  structureIds: ReadonlyArray<number>,
  escalatedStructureIds: ReadonlyArray<number>,
  targetId: number,
  parentsOf: (id: number) => ReadonlyArray<number>,
): boolean {
  if (structureIds.includes(targetId)) return true;
  return escalatedStructureIds.some((cid) => parentsOf(cid).includes(targetId));
}
